import React, { useRef, useEffect, useState, useContext } from "react";
import { useOktaAuth } from "@okta/okta-react";
import * as OktaSignIn from "@okta/okta-signin-widget";
import "@okta/okta-signin-widget/dist/css/okta-sign-in.min.css";
import { RCLContext } from "../../components/RCL/RCLProvider";
import { getURLQueries } from "../../helpers/helper";
import config from "../../app/config";
import "./OktaSignInWidget.scss";
import BrandSelection from "./BrandSelection";
import OverwriteSigninButton from "./OverwriteSigninButton";

import ReactDOM from "react-dom";

const OktaSignInWidget = ({ setCorsErrorModalOpen }) => {
  const widgetRef = useRef(null);

  const { oktaAuth } = useOktaAuth();
  const { locale } = useContext(RCLContext);
     
  const handleInputKeydown =(e) => {
    if(e.code === 'Enter'){
        const hasSelectedBrand = sessionStorage.getItem("selected-brand")?.length > 0;
        if(!hasSelectedBrand){
          e.preventDefault();

          const signinBrandSelectElement = document.getElementById("signin-brand-selector");
          const signinBrandError = document.getElementById("signin-brand-error");

          signinBrandError.style.display = "block";
          signinBrandSelectElement.style.borderColor = "#B00121";

          return false;
        }
    }
  }

  const injectAdditionalElement = () => {

    const formContainerElement = document.querySelector(".o-form-content"); 
    const fieldsetContainerElement = formContainerElement.querySelector(".o-form-fieldset-container");
    const firstFieldsetElement = fieldsetContainerElement.querySelector(".o-form-fieldset");
    const secondFieldsetElement = firstFieldsetElement.nextSibling;
    const buttonBarElement = document.querySelector(".o-form-button-bar");
    const buttonElement = document.getElementById("okta-signin-submit");

    //To add brand drop down 
    const brandWrapperElement = document.createElement("div"); 
    brandWrapperElement.className = "o-form-fieldset o-form-label-top margin-top-0";
    brandWrapperElement.style.marginTop = '-25px';

    const dropdownWrapperElement = document.createElement("div");
    dropdownWrapperElement.id = "custom-multiple-dropdown";
    dropdownWrapperElement.className = "custom-multiple-dropdown";
    brandWrapperElement.appendChild(dropdownWrapperElement);
    
    fieldsetContainerElement.insertBefore(brandWrapperElement,secondFieldsetElement.nextSibling);     	  
    ReactDOM.render(<BrandSelection locale={locale}/>, document.getElementById("custom-multiple-dropdown"));		

    //To customize email/password input field 
    const emailElement = document.getElementById("okta-signin-username");
    const passwordElement = document.getElementById("okta-signin-password");
    emailElement.addEventListener("keydown", handleInputKeydown);
    passwordElement.addEventListener("keydown", handleInputKeydown);

    //To add overwrite sign in button 
    const overwriteSigninWrapperElement = document.createElement("div"); 
    overwriteSigninWrapperElement.className = "button";

    const buttonWrapperElement = document.createElement("div");
    buttonWrapperElement.id = "custom-signin-button";
    buttonWrapperElement.className = "custom-signin-button";
    brandWrapperElement.appendChild(buttonWrapperElement);

    buttonBarElement.insertBefore(buttonWrapperElement, buttonElement);    
    ReactDOM.render(<OverwriteSigninButton />, document.getElementById("custom-signin-button"));	
    buttonElement.style.display = "none";
  };
    useEffect(() => {
      if (!widgetRef.current) {
        return false;
      }

      const { issuer, clientId, redirectUri, scopes, useInteractionCode } =
        config.oidc;
      const redirectUrl= `${redirectUri}/${locale}/login/callback`;  
      const widget = new OktaSignIn({
        /**
         * Note: when using the Sign-In Widget for an OIDC flow, it still
         * needs to be configured with the base URL for your Okta Org. Here
         * we derive it from the given issuer for convenience.
         */
        baseUrl: issuer.split("/oauth2")[0],
        language: locale,
        registration: {
          click: function () {
              window.location.href = window.location.origin + `/${locale}/signup`;
          }
        },
        features: {
            registration: true,
            showPasswordToggleOnSignInPage: true,
            selfServiceUnlock: true
        },
        clientId,
        redirectUri:redirectUrl,
        i18n: {
          en: {
            "primaryauth.title": "Sign In",
            "help":" ",
            "primaryauth.username.placeholder": "Email",
            "error.username.required": "Please enter email.",
            "error.password.required": "Please enter password.",
          },
          fr:{
            "help":" ",
            "primaryauth.username.placeholder": "Courriel",
            "error.username.required": "Veuillez saisir votre adresse e-mail.",
            "error.password.required": "Veuillez entrer le mot de passe.",
          },
        },
        authParams: {
          // To avoid redirect do not set "pkce" or "display" here. OKTA-335945
          issuer,
          scopes,
        },
        useInteractionCodeFlow: useInteractionCode, // Set to true, if your org is OIE enabled
        recoveryToken: getURLQueries("token"),
        theme: {
          tokens: {
            PalettePrimaryMain: '#D11DCA',
            TypographyColorBody: '#00297A',
            TypographyColorHeading: '#00297A',
            TypographyFamilyHeading: 'Helvetica',
            TypographyFamilyBody: 'Helvetica',
            TypographyWeightHeading: 600,
            BorderRadiusMain: '24px',
            Spacing5: '2.85714286rem',
            BorderColorDangerLight: 'red',
          }
        }
      });
  
      widget.renderEl(
        { el: widgetRef.current },
        (res) => {
          oktaAuth.handleLoginRedirect(res.tokens);
          injectAdditionalElement();   
        },
        (err) => {
          throw err;
        }
      );

      // Note: Can't distinguish CORS error from other network errors
      const isCorsError = (err) => err.name === "AuthApiError" && !err.statusCode;

      widget.on("afterError", (_context, error) => {
        if (isCorsError(error)) {
          setCorsErrorModalOpen(true);
        }
        let formErrorContainer = document.querySelector(".o-form-error-container.o-form-has-errors");
        if(formErrorContainer)
          formErrorContainer.style.display = "block";
      });

      widget.on('afterRender', function (_context) { 
        if (_context.controller === 'primary-auth') {
          injectAdditionalElement();              
        }
        return;
      }); 

    return () => widget.remove();
  }, [oktaAuth, setCorsErrorModalOpen, locale]);

  return <div id="okta-sign-in-container" ref={widgetRef} />;
};

export default OktaSignInWidget;
export { OktaSignInWidget };
